import { Instagram, Linkedin, Facebook } from "lucide-react";
import androidBadge from "../src/assets/googlebadge.svg";
import appleBadge from "../src/assets/appstorebadge.svg";
import anamvrLogo from "../src/assets/AnamVR-Icon.webp";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black text-white py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Column 1: Logo and company info */}
          <div>
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mr-3">
                <img
                  src={anamvrLogo || "/placeholder.svg"}
                  alt="AnamVR Logo"
                  className="w-full h-full rounded-md"
                />
              </div>
              <span className="text-white text-xl font-bold">AnamVR</span>
            </div>
            <p className="text-gray-400 text-sm mb-6">
              Immersive mental health support through VR technology.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.linkedin.com/company/anamvr/"
                aria-label="LinkedIn"
                className="text-gray-400 hover:text-white transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin size={18} />
              </a>
              <a
                href="https://www.instagram.com/anamvrhealthcare/"
                aria-label="Instagram"
                className="text-gray-400 hover:text-white transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://www.facebook.com/anamvrhealthcare/"
                aria-label="Facebook"
                className="text-gray-400 hover:text-white transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Column 2: Main Navigation */}
          <div>
            <h3 className="text-white font-semibold mb-4">Navigation</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="/"
                  aria-label="Homepage"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href="https://shop.anamvr.com"
                  aria-label="Shop"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  Shop
                </a>
              </li>
              <li>
                <a
                  href="/blog"
                  aria-label="Blog"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  Blog
                </a>
              </li>

              <li>
                <a
                  href="/for-individuals"
                  aria-label="For Individuals"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  For Individuals
                </a>
              </li>
              <li>
                <a
                  href="/for-organisations"
                  aria-label="For Organisations"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  For Organisations
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3: About AnamVR */}
          <div>
            <h3 className="text-white font-semibold mb-4">About AnamVR</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="/about/mission"
                  aria-label="Our Mission"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  Our Mission
                </a>
              </li>
              <li>
                <a
                  href="/about/vision"
                  aria-label="Our Vision & Pillars"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  Our Vision & Pillars
                </a>
              </li>
              <li>
                <a
                  href="/about/story"
                  aria-label="Our Story & Impact"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  Our Story & Impact
                </a>
              </li>
              <li>
                <a
                  href="/about/team"
                  aria-label="Meet the Team"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  Meet the Team
                </a>
              </li>
              <li>
                <a
                  href="/about/contributors"
                  aria-label="Contributors & Collaborators"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  Contributors & Collaborators
                </a>
              </li>
              <li>
                <a
                  href="/about/research"
                  aria-label="Research & Evidence"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  Research & Evidence
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Support */}
          <div>
            <h3 className="text-white font-semibold mb-4">Support</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="/contact"
                  aria-label="Help Center"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  Contact and FAQs
                </a>
              </li>
            </ul>
          </div>

          {/* Column 5: Download */}
          <div>
            <h3 className="text-white font-semibold mb-4">Download Our App</h3>
            <div className="flex flex-col space-y-3 mb-6">
              <a
                href="https://apps.apple.com/ie/app/anamvr/id6499339767"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download on the App Store"
              >
                <img
                  src={appleBadge || "/placeholder.svg"}
                  alt="Download on the App Store"
                  className="h-10 w-auto"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.anamvr&hl=en"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get it on Google Play"
              >
                <img
                  src={androidBadge || "/placeholder.svg"}
                  alt="Get it on Google Play"
                  className="h-10 w-auto"
                />
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-800 w-full mb-6"></div>

        {/* Copyright row */}
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="text-gray-500 text-sm mb-4 md:mb-0">
            © {currentYear} AnamVR. All rights reserved.
          </div>

          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <a
              href="/privacy-policy"
              aria-label="Privacy Policy"
              className="text-gray-500 hover:text-white transition-colors text-sm"
            >
              Privacy Policy
            </a>
            <a
              href="/terms"
              aria-label="Terms of Service"
              className="text-gray-500 hover:text-white transition-colors text-sm"
            >
              Terms of Service
            </a>
            <a
              href="/cookies"
              aria-label="Cookie Policy"
              className="text-gray-500 hover:text-white transition-colors text-sm"
            >
              Cookie Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
